import { useCallback, useEffect, useMemo, useRef, useState, memo } from 'react';
import { UnstyledButton } from '@mantine/core';
import type { Line } from '@nelnela/contracts';
import { fmtTime, isWhitespace, speakerHue, tokenize, type Token } from '../../lib/text';
import { chunkAt, type Marks } from '../../lib/marks';
import { lineSpans, type PhraseSelection, type TokenRef } from '../../lib/selection';
import { SWIPE_CLICK_GUARD_MS } from '../../lib/save-gesture';

export type EnMode = 'show' | 'reveal' | 'hide';

export interface Selection {
  lineId: string;
  charStart: number;
  charEnd: number;
}

/** how far the pointer must travel before a swipe engages (and past which a tap is no tap) */
const SWIPE_SLOP_PX = 8;
// `SWIPE_CLICK_GUARD_MS` (a click this soon after a swipe ended is the swipe's own tail —
// swallowed) moved to `lib/save-gesture.ts` in M6e: the double-tap-to-save window has to be
// stated against it, and two constants that must agree should not live in two files.

export interface TranscriptProps {
  lines: Line[];
  activeIdx: number;
  enMode: EnMode;
  revealed: ReadonlySet<string>;
  /** single-line highlight (M2) — ignored while `phrase` is set */
  selection: Selection | null;
  /** M3 phrase span (principle 6): highlighted via `lineSpans` on every row it touches */
  phrase?: PhraseSelection | null;
  /** M4a marks (D2): dotted weak words + solid chunks, per line id. Absent = draw nothing. */
  marks?: ReadonlyMap<string, Marks>;
  /** M5 (Me → Georgian text size), 16–18. Absent = READING_SURFACE_CSS's own 17px fallback. */
  fontSizePx?: number;
  /** tap on a chunk (a multi-token item) — the sheet opens its card first (spec § 3) */
  onChunkTap?: (itemId: string, line: Line, charStart: number, charEnd: number) => void;
  /**
   * What sits above each row.
   *
   * - `'time'` (default, Watch): the timestamp, the speaker chip beside it, and "jump here".
   * - `'none'` (M6a, the story reader `5a`): nothing — a story sentence has no timeline and no
   *   voice, and "0:00 · jump here" over every one would be a control that does nothing.
   * - `'speaker'` (M6b, a dialogue piece): the chip alone. A piece HAS voices but no timeline,
   *   so the name and its hue are the only part of the gutter that means anything, and the seek
   *   affordance must not appear — there is nothing to seek.
   */
  gutter?: 'time' | 'none' | 'speaker';
  /** tap on the time gutter / speaker chip — a text tap never seeks (04-flows F4).
   *  Optional: with `gutter: 'none'` or `'speaker'` there is nothing that could call it. */
  onSeek?: (line: Line) => void;
  /** tap on a word token → gloss */
  onWord?: (token: Token, line: Line) => void;
  /** same tap, with the token's index into `tokenize(line.text)` (= `data-tok`) */
  onWordTap?: (token: Token, line: Line, tokIdx: number) => void;
  /** tap on the ••• pill (EN tap-to-reveal) */
  onReveal: (line: Line) => void;
  /** swipe across tokens (horizontal drag with axis lock): fires once when the lock engages … */
  onSwipeStart?: () => void;
  /** … then on every change of the token under the pointer (`a` = where the drag began) … */
  onSwipe?: (a: TokenRef, b: TokenRef) => void;
  /** … and once when the pointer lifts. A drag that never locks fires none of these. */
  onSwipeEnd?: () => void;
  /** row-end affordance (F4 "＋ save line"), right-aligned in the gutter row. Keep it referentially
   *  stable (useCallback) — rows are memoised and re-render whenever this identity changes. */
  renderRowEnd?: (line: Line) => React.ReactNode;
  style?: React.CSSProperties;
}

/**
 * The synced transcript (F4; reading-surface-spec §1–4). Ported from the local app's
 * Transcript.jsx: rows with time gutter + speaker chip, whitespace tokenization with Unicode
 * punctuation trim, auto-follow with a "resume follow" pill. Differences from the local app:
 * every whitespace token is its own tap target (never OS text selection — principle 6), the
 * container is the scroll box in every layout, and auto-follow scrolls ONLY when the current line
 * would leave the visible band (spec §4) instead of on every line.
 *
 * M3: phrase highlight across rows (`phrase`), swipe-across-tokens with axis lock (`onSwipe*`),
 * `onWordTap` with the token index, and a `renderRowEnd` slot.
 */
export function Transcript({
  lines,
  activeIdx,
  enMode,
  revealed,
  selection,
  phrase,
  marks,
  fontSizePx,
  onChunkTap,
  gutter = 'time',
  onSeek,
  onWord,
  onWordTap,
  onReveal,
  onSwipeStart,
  onSwipe,
  onSwipeEnd,
  renderRowEnd,
  style,
}: TranscriptProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [follow, setFollow] = useState(true);

  const scrollActiveIntoView = useCallback((behavior: ScrollBehavior) => {
    const c = containerRef.current;
    if (!c) return;
    const el = c.querySelector<HTMLElement>('[data-active="true"]');
    if (!el) return;
    // Manual math instead of scrollIntoView so only the transcript box scrolls — scrollIntoView
    // would also scroll ancestors and drag the pinned player off-screen on mobile.
    const cr = c.getBoundingClientRect();
    const r = el.getBoundingClientRect();
    const target = c.scrollTop + (r.top - cr.top) - (c.clientHeight - r.height) / 2;
    c.scrollTo({ top: Math.max(0, target), behavior });
  }, []);

  // Auto-follow: only when the active row is outside the comfortable band of the viewport
  // (top 15% … bottom 65%), so a line-by-line playthrough doesn't twitch the list every second.
  useEffect(() => {
    if (!follow || activeIdx === -1) return;
    const c = containerRef.current;
    if (!c) return;
    const el = c.querySelector<HTMLElement>('[data-active="true"]');
    if (!el) return;
    const cr = c.getBoundingClientRect();
    const r = el.getBoundingClientRect();
    const bandTop = cr.top + cr.height * 0.15;
    const bandBottom = cr.top + cr.height * 0.65;
    if (r.top < bandTop || r.bottom > bandBottom) scrollActiveIntoView('smooth');
  }, [activeIdx, follow, scrollActiveIntoView]);

  // Hand-scrolling pauses auto-follow. Programmatic scrolls (ours) don't fire these events.
  // Touch: only a real drag (> 8 px vertical), not the sub-pixel wobble of an ordinary tap.
  const pauseFollow = useCallback(() => setFollow(false), []);
  const touchStartY = useRef<number | null>(null);

  // Phrase highlight: one range per row the span touches (rows are memoised — each gets only its
  // own span, computed once per phrase change). Phrase wins over the single-line `selection`.
  const phraseSpans = useMemo(() => {
    if (!phrase) return null;
    const m = new Map<string, Selection>();
    for (const s of lineSpans(phrase, lines)) m.set(s.lineId, s);
    return m;
  }, [phrase, lines]);

  // ------------------------------------------------ swipe across tokens (axis lock)
  // Delegated on the scroll container: pointerdown over a `.nn-w` remembers where; the first move
  // decides the axis (|dx| > slop && |dx| > |dy| → horizontal lock; a vertical move gives up and
  // lets the browser scroll — `.nn-reading` has `touch-action: pan-y`, so a horizontal touch drag
  // keeps sending pointer events while a vertical one becomes a scroll + pointercancel). Once
  // locked we capture the pointer and track the token under it with elementFromPoint, reporting
  // each change; the trailing click is swallowed so a swipe never also fires a word tap.
  const dragRef = useRef<{
    pointerId: number;
    a: TokenRef;
    x: number;
    y: number;
    locked: boolean;
    dead: boolean;
    last: TokenRef | null;
  } | null>(null);
  const clickGuardUntilRef = useRef(0);

  const tokenAt = (el: Element | null): TokenRef | null => {
    const w = el?.closest?.('.nn-w') as HTMLElement | null;
    if (!w) return null;
    const row = w.closest('[data-line-id]') as HTMLElement | null;
    const lineId = row?.dataset.lineId;
    const tokIdx = Number(w.dataset.tok);
    if (!lineId || !Number.isFinite(tokIdx)) return null;
    return { lineId, tokIdx };
  };
  const endDrag = (e: React.PointerEvent<HTMLDivElement>) => {
    const d = dragRef.current;
    if (!d || d.pointerId !== e.pointerId) return;
    dragRef.current = null;
    if (!d.locked) return;
    clickGuardUntilRef.current = Date.now() + SWIPE_CLICK_GUARD_MS;
    try {
      if (e.currentTarget.hasPointerCapture(e.pointerId))
        e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      /* already released */
    }
    onSwipeEnd?.();
  };
  const swipeEnabled = !!(onSwipe || onSwipeStart || onSwipeEnd);

  return (
    <div style={{ position: 'relative', minHeight: 0, ...style }}>
      <div
        ref={containerRef}
        className="nn-reading"
        role="list"
        onWheel={pauseFollow}
        onTouchStart={(e) => {
          touchStartY.current = e.touches[0]?.clientY ?? null;
        }}
        onTouchMove={(e) => {
          const y = e.touches[0]?.clientY;
          if (
            touchStartY.current !== null &&
            y !== undefined &&
            Math.abs(y - touchStartY.current) > 8
          )
            pauseFollow();
        }}
        onKeyDown={(e) => {
          if (['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End'].includes(e.key))
            pauseFollow();
        }}
        onContextMenu={(e) => e.preventDefault()}
        onPointerDown={(e) => {
          if (!swipeEnabled || !e.isPrimary || e.button !== 0) return;
          const a = tokenAt(e.target as Element);
          if (!a) return;
          dragRef.current = {
            pointerId: e.pointerId,
            a,
            x: e.clientX,
            y: e.clientY,
            locked: false,
            dead: false,
            last: a,
          };
        }}
        onPointerMove={(e) => {
          const d = dragRef.current;
          if (!d || d.pointerId !== e.pointerId || d.dead) return;
          if (!d.locked) {
            const dx = e.clientX - d.x;
            const dy = e.clientY - d.y;
            if (Math.abs(dx) > SWIPE_SLOP_PX && Math.abs(dx) > Math.abs(dy)) {
              d.locked = true;
              try {
                e.currentTarget.setPointerCapture(e.pointerId);
              } catch {
                /* capture is best-effort */
              }
              onSwipeStart?.();
              onSwipe?.(d.a, d.last ?? d.a);
            } else if (Math.abs(dy) > SWIPE_SLOP_PX) {
              d.dead = true; // vertical: the browser scrolls; a later wobble must not lock
              return;
            } else return;
          }
          const b = tokenAt(document.elementFromPoint(e.clientX, e.clientY));
          if (!b) return; // over whitespace / gutter / punctuation: keep the last token
          if (d.last && d.last.lineId === b.lineId && d.last.tokIdx === b.tokIdx) return;
          d.last = b;
          onSwipe?.(d.a, b);
        }}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        onClickCapture={(e) => {
          if (Date.now() < clickGuardUntilRef.current) {
            clickGuardUntilRef.current = 0;
            e.stopPropagation();
            e.preventDefault();
          }
        }}
        style={
          {
            position: 'absolute',
            inset: 0,
            overflowY: 'auto',
            overscrollBehavior: 'contain',
            WebkitOverflowScrolling: 'touch',
            paddingBottom: 'calc(40vh + env(safe-area-inset-bottom))',
            // ONE style object, not two. M5 set `--nn-transcript-size` through a second, spread
            // `style` prop on this same element; JSX keeps the LAST prop of a name, so the
            // literal below silently won and the custom property was never emitted — which is to
            // say M5's "Georgian text size" setting has done nothing on Watch since it shipped.
            // Found in M6a while giving the story reader its own typography.
            // `READING_SURFACE_CSS` reads the variable with a 17px fallback, so an absent
            // `fontSizePx` must leave it unset rather than write `undefined`.
            ...(fontSizePx ? { '--nn-transcript-size': `${fontSizePx}px` } : {}),
          } as React.CSSProperties
        }
      >
        {lines.map((line, idx) => (
          <TranscriptRow
            key={line.id}
            line={line}
            idx={idx}
            active={idx === activeIdx}
            enMode={enMode}
            revealed={revealed.has(line.id)}
            selection={
              phraseSpans
                ? (phraseSpans.get(line.id) ?? null)
                : selection && selection.lineId === line.id
                  ? selection
                  : null
            }
            marks={marks?.get(line.id)}
            onChunkTap={onChunkTap}
            gutter={gutter}
            onSeek={onSeek}
            onWord={onWord}
            onWordTap={onWordTap}
            onReveal={onReveal}
            renderRowEnd={renderRowEnd}
          />
        ))}
        {lines.length === 0 && (
          <div style={{ padding: 24, color: 'var(--nn-muted)', fontSize: 14 }}>
            No transcript lines yet.
          </div>
        )}
      </div>
      {!follow && activeIdx !== -1 && (
        <UnstyledButton
          onClick={() => {
            setFollow(true);
            scrollActiveIntoView('smooth');
          }}
          style={{
            position: 'absolute',
            left: '50%',
            bottom: 14,
            transform: 'translateX(-50%)',
            padding: '7px 14px',
            borderRadius: 999,
            background: 'var(--nn-panel-2)',
            border: '1px solid var(--nn-hairline)',
            color: 'var(--nn-text)',
            fontSize: 12.5,
            boxShadow: 'var(--mantine-shadow-sm)',
            whiteSpace: 'nowrap',
          }}
        >
          ⤓ resume follow
        </UnstyledButton>
      )}
    </div>
  );
}

interface RowProps {
  line: Line;
  idx: number;
  active: boolean;
  enMode: EnMode;
  revealed: boolean;
  selection: Selection | null;
  marks?: Marks | undefined;
  onChunkTap?: (itemId: string, line: Line, charStart: number, charEnd: number) => void;
  gutter: 'time' | 'none' | 'speaker';
  onSeek?: (line: Line) => void;
  onWord?: (token: Token, line: Line) => void;
  onWordTap?: (token: Token, line: Line, tokIdx: number) => void;
  onReveal: (line: Line) => void;
  renderRowEnd?: (line: Line) => React.ReactNode;
}

/**
 * One row of the reading surface — exported since M6e §6 so the story's end checks can render a
 * GEORGIAN question through the same component its sentences use. A hard word in the question is
 * then a lookup rather than a wall, which is the whole reason the checks moved into Georgian.
 *
 * Everything a row needs it takes as a prop, so rendering one outside the transcript costs a
 * `Line` and nothing else. Wrap it in `.nn-reading` for the no-select / touch-action / font-size
 * rules (`READING_SURFACE_CSS`); the token classes themselves are global.
 */
export const TranscriptRow = memo(function TranscriptRow({
  line,
  idx,
  active,
  enMode,
  revealed,
  selection,
  marks,
  onChunkTap,
  gutter,
  onSeek,
  onWord,
  onWordTap,
  onReveal,
  renderRowEnd,
}: RowProps) {
  const tokens = useMemo(() => tokenize(line.text), [line.text]);
  const hue = line.speaker ? speakerHue(line.speaker) : null;
  const rowEnd = renderRowEnd?.(line);
  return (
    <div
      role="listitem"
      data-idx={idx}
      data-line-id={line.id}
      data-active={active ? 'true' : undefined}
      className={`nn-line${active ? ' nn-line--playing' : ''}`}
      style={{ padding: '8px 14px' }}
    >
      {/* With `gutter: 'none'` the whole row goes unless something needs the row-end slot: a
          story sentence has no timestamp and no speaker, and rendering "0:00 · jump here" above
          every one of them would be a control that does nothing, on a surface (`5a`) that has
          none. `'speaker'` keeps the chip and drops the rest. */}
      {(gutter === 'time' ||
        (gutter === 'speaker' && line.speaker) ||
        (rowEnd != null && rowEnd !== false)) && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
          {/* A piece has voices but no timeline: the chip is not a button, because there is
              nothing behind it to jump to. Same hue rule as Watch (spec §1), so the same
              character reads as the same colour wherever they appear. */}
          {gutter === 'speaker' && line.speaker && (
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                minHeight: 22,
                fontSize: 10.5,
              }}
            >
              <span
                aria-hidden
                style={{
                  width: 7,
                  height: 7,
                  borderRadius: '50%',
                  background: `light-dark(oklch(0.55 0.09 ${hue}), oklch(0.72 0.08 ${hue}))`,
                }}
              />
              <span style={{ color: 'var(--nn-muted)' }}>{line.speaker}</span>
            </span>
          )}
          {gutter === 'time' && (
            <UnstyledButton
              onClick={() => onSeek?.(line)}
              title="jump here"
              aria-label={`Jump to ${fmtTime(line.startMs)}`}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                minHeight: 22,
                fontSize: 10.5,
                color: 'var(--nn-faint)',
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              <span>{fmtTime(line.startMs)}</span>
              {line.speaker && (
                <>
                  <span
                    aria-hidden
                    style={{
                      width: 7,
                      height: 7,
                      borderRadius: '50%',
                      background: `light-dark(oklch(0.55 0.09 ${hue}), oklch(0.72 0.08 ${hue}))`,
                    }}
                  />
                  <span style={{ color: 'var(--nn-muted)' }}>{line.speaker}</span>
                </>
              )}
            </UnstyledButton>
          )}
          {rowEnd != null && rowEnd !== false && (
            <>
              <span style={{ flex: 1 }} />
              <span style={{ display: 'inline-flex', alignItems: 'center', flexShrink: 0 }}>
                {rowEnd}
              </span>
            </>
          )}
        </div>
      )}
      <div lang="ka" style={{ color: active ? 'var(--nn-text-strong)' : 'var(--nn-text)' }}>
        {tokens.map((t, i) => {
          if (isWhitespace(t)) return t.raw;
          if (!t.word) return <span key={i}>{t.raw}</span>; // punctuation only: not tappable
          const selected =
            selection !== null &&
            t.charStart >= selection.charStart &&
            t.charEnd <= selection.charEnd;
          // D2: exactly two marks. A chunk covers several tokens and is ONE tap target — every
          // token inside it carries `.nn-chunk` and the tap opens the chunk's card, not the word's
          // (reading-surface-spec § 3: "tap inside a chunk → chunk card first").
          const chunk = marks ? chunkAt(marks, i) : null;
          const weak = marks?.weak.has(i) === true;
          const cls =
            `nn-w${selected ? ' nn-sel' : ''}` + (chunk ? ' nn-chunk' : weak ? ' nn-w--weak' : '');
          return (
            <span
              key={i}
              className={cls}
              data-tok={i}
              data-mark={chunk ? 'chunk' : weak ? 'weak' : undefined}
              role="button"
              tabIndex={-1}
              onClick={() => {
                if (chunk && onChunkTap) {
                  onChunkTap(chunk.itemId, line, chunk.charStart, chunk.charEnd);
                  return;
                }
                onWord?.(t, line);
                onWordTap?.(t, line, i);
              }}
            >
              {t.raw}
            </span>
          );
        })}
      </div>
      <EnSlot line={line} enMode={enMode} revealed={revealed} onReveal={onReveal} />
    </div>
  );
});

function EnSlot({
  line,
  enMode,
  revealed,
  onReveal,
}: {
  line: Line;
  enMode: EnMode;
  revealed: boolean;
  onReveal: (line: Line) => void;
}) {
  if (enMode === 'hide') return null;
  if (!line.translation) {
    // Untranslated: "translation pending" italic in that slot (spec §6) whenever EN is on.
    return (
      <div style={{ fontSize: 12, fontStyle: 'italic', color: 'var(--nn-faint)', marginTop: 3 }}>
        translation pending
      </div>
    );
  }
  if (enMode === 'show' || revealed) {
    return (
      <div style={{ fontSize: 13, lineHeight: 1.5, color: 'var(--nn-muted)', marginTop: 3 }}>
        {line.translation}
      </div>
    );
  }
  return (
    <div style={{ marginTop: 4 }}>
      <UnstyledButton
        onClick={() => onReveal(line)}
        aria-label="Reveal translation"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          minWidth: 40,
          minHeight: 22,
          padding: '2px 0',
          borderRadius: 999,
          background: 'var(--nn-hairline)',
          border: '1px solid var(--nn-hairline)',
          color: 'var(--nn-faint)',
          fontSize: 10,
          letterSpacing: 2,
        }}
      >
        •••
      </UnstyledButton>
    </div>
  );
}
